import React, { useRef } from 'react';
import { X, Printer, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { formatTanggalIndo } from '@/lib/timeUtils';
import { terbilangRupiah } from '@/lib/terbilang';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const ROMAN_MONTHS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

export default function ClientInvoicePreviewModal({
  isOpen,
  onClose,
  products = [],
  legalitas = [],
  activeBrand,
  clientUser,
  matchedClient,
  totalHargaJualSemua = 0,
  onProceedToCheckout
}) {
  const invoiceRef = useRef(null);

  const { data: companySettingsList } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });
  const localSettings = companySettingsList?.[0] || {};
  const companySettings = {
    nama_perusahaan: localSettings.nama_perusahaan || 'PT. SATALA DERMATECH ESSENTIAL',
    alamat_perusahaan: localSettings.alamat_perusahaan || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat',
    logo_url: localSettings.logo_url || '/logo-invoice.png',
    stempel_url: localSettings.stempel_url || '/stempel-invoice.png',
    ttd_url: localSettings.ttd_url || '/ttd-invoice.png',
    nama_direktur: localSettings.nama_direktur || 'Umar Syarif',
  };

  if (!isOpen) return null;

  const now = new Date();
  const currentMonthRoman = ROMAN_MONTHS[now.getMonth()];
  const cleanBrandCode = (activeBrand || clientUser?.brand_name || 'STL')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6) || 'STL';

  const invoiceNo = `INV-${cleanBrandCode}-${currentMonthRoman}-003`;

  // Determine Payment Term
  let paymentTerm = 'TEMPO';
  if (matchedClient) {
    const terms = matchedClient?.allowed_payment_terms || [];
    const isTempo = terms.includes('tempo') || matchedClient?.is_tempo;
    const isCash = terms.includes('cash') || terms.includes('dp');
    if (isTempo && !isCash) paymentTerm = 'TEMPO';
    else if (isCash && !isTempo) paymentTerm = 'CASH (DP 50% + 50%)';
    else if (isTempo) paymentTerm = 'TEMPO';
    else paymentTerm = 'CASH (DP 50% + 50%)';
  } else if (clientUser?.termin_pembayaran) {
    paymentTerm = clientUser.termin_pembayaran.toUpperCase();
  }

  // Filter valid products that have a name
  const validProducts = products.filter(p => p.nama_product || (parseInt(p.qty) || 0) > 0);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm overflow-y-auto animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl border border-[#E6D5B8] flex flex-col my-auto max-h-[95vh] overflow-hidden">
        
        {/* Action Bar Header (Screen only) */}
        <div className="px-6 py-4 border-b border-[#E6D5B8] bg-[#F9F5EC] flex items-center justify-between no-print">
          <div className="flex items-center gap-2">
            <span className="bg-[#8C6208] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Pratinjau Invoice Resmi
            </span>
            <span className="text-xs text-[#7A695B] font-semibold">
              No: <strong className="text-[#302621]">{invoiceNo}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="border-[#E6D5B8] text-[#302621] hover:bg-white text-xs font-bold h-9 gap-1.5 rounded-xl shadow-xs"
            >
              <Printer className="w-4 h-4 text-[#8C6208]" />
              Cetak / Simpan PDF
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="rounded-full w-9 h-9 text-[#7A695B] hover:text-[#302621] hover:bg-[#E6D5B8]/40"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Printable Invoice Sheet (Sesuai Gambar 2 Resmi) */}
        <div className="p-6 sm:p-10 overflow-y-auto flex-1 bg-white" id="printable-invoice" ref={invoiceRef}>
          
          {/* Header Satala + Status Badge */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex gap-4 items-center">
              <img 
                src={companySettings.logo_url || '/logo-invoice.png'} 
                alt="Logo" 
                className="w-14 h-14 object-contain shrink-0" 
                onError={(e) => { e.target.onerror = null; e.target.src = '/logo.png'; }}
              />
              <div>
                <h2 className="text-base font-black uppercase text-[#302621] tracking-tight">
                  {companySettings.nama_perusahaan || 'PT. SATALA DERMATECH ESSENTIAL'}
                </h2>
                <p className="text-[9px] sm:text-[10px] text-slate-500 max-w-[440px] leading-relaxed mt-0.5">
                  {companySettings.alamat_perusahaan || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat'}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <h1 className="text-2xl font-black text-slate-800 tracking-wider">INVOICE</h1>
              <span className="inline-block mt-1 border border-green-500 text-green-700 bg-white font-bold px-3 py-0.5 rounded text-[10px] tracking-wider uppercase">
                BELUM BAYAR
              </span>
            </div>
          </div>

          {/* Divider line in Gold */}
          <div className="border-b-[3px] border-[#D4AF37] mb-5"></div>

          {/* Meta Grid (Nomor, Tanggal, Payment Term & Kepada) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div className="space-y-1 text-xs">
              <div className="flex">
                <span className="w-28 text-slate-500">Nomor</span>
                <span className="w-2 text-slate-600">:</span>
                <span className="font-bold text-slate-800">{invoiceNo}</span>
              </div>
              <div className="flex">
                <span className="w-28 text-slate-500">Tanggal</span>
                <span className="w-2 text-slate-600">:</span>
                <span className="text-slate-800">{formatTanggalIndo(now)}</span>
              </div>
              <div className="flex">
                <span className="w-28 text-slate-500">Payment Term</span>
                <span className="w-2 text-slate-600">:</span>
                <span className="font-bold text-slate-800 uppercase">{paymentTerm}</span>
              </div>
            </div>

            <div className="border border-slate-200 bg-slate-50/50 p-2.5 rounded-lg text-xs">
              <p className="font-bold text-slate-800 mb-0.5">Kepada:</p>
              <p className="font-bold text-[#8C6208] text-sm">{activeBrand || clientUser?.brand_name || clientUser?.company_name || 'Mitra'}</p>
              <p className="text-[9px] text-slate-500 mt-1 leading-tight">
                Pembelian produk OEM / Maklon Satala Dermatech
              </p>
              <p className="text-[9px] text-slate-500">Di Tempat.</p>
            </div>
          </div>

          {/* Salutation */}
          <div className="mb-4 text-xs">
            <p className="font-bold text-slate-700">Dengan hormat,</p>
            <p className="text-slate-500 text-[10px] mt-0.5">
              Berdasarkan kesepakatan bersama, dengan ini kami mengajukan invoice tersebut sebagai berikut :
            </p>
          </div>

          {/* Table Items */}
          <table className="w-full text-left border-collapse mb-4 text-[10px]">
            <thead>
              <tr className="bg-[#302621] text-[#D4AF37]">
                <th className="py-2 px-3 font-bold w-8 text-center rounded-tl-md">No</th>
                <th className="py-2 px-3 font-bold">Nama Produk / Layanan</th>
                <th className="py-2 px-3 font-bold">Keterangan</th>
                <th className="py-2 px-3 font-bold text-center w-12">Qty</th>
                <th className="py-2 px-3 font-bold text-right w-28">Harga/Pcs</th>
                <th className="py-2 px-3 font-bold text-right w-28 rounded-tr-md">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {validProducts.map((item, idx) => {
                const qty = Math.max(0, parseInt(item.qty) || 0);
                const unitP = item.custom_harga_jual || item.harga_jual_per_pcs || 0;
                const subtotal = qty * unitP;
                const size = item.selected_size || item.botol_ukuran_client || 0;

                return (
                  <tr key={`p-${idx}`} className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                    <td className="py-1.5 px-2 text-center text-slate-500">{idx + 1}</td>
                    <td className="py-1.5 px-2 font-semibold text-slate-800">{item.nama_product || `Produk ${idx + 1}`}</td>
                    <td className="py-1.5 px-2 text-slate-500 text-[9px]">
                      {size > 0 ? `Volume ${size} ml` : 'Ukuran Standar'}
                    </td>
                    <td className="py-1.5 px-2 text-center font-medium">{qty}</td>
                    <td className="py-1.5 px-2 text-right">Rp {fmt(unitP)}</td>
                    <td className="py-1.5 px-2 text-right font-semibold text-slate-800">Rp {fmt(subtotal)}</td>
                  </tr>
                );
              })}

              {/* Legalitas jika ada */}
              {(() => {
                const flattenedLegalitas = [];
                legalitas.forEach((leg) => {
                  const hargaJual = Number(leg.harga_jual !== undefined && leg.harga_jual !== '' ? leg.harga_jual : (leg.nominal || 0));
                  const qty = leg.qty_variant || (Array.isArray(leg.selected_variants) && leg.selected_variants.length > 0 ? leg.selected_variants.length : 1);
                  flattenedLegalitas.push({
                    nama: leg.nama,
                    keterangan: 'Layanan Legalitas Maklon',
                    qty: qty,
                    harga: hargaJual,
                    subtotal: hargaJual * qty,
                  });
                });

                const startIdx = validProducts.length;
                return flattenedLegalitas.map((item, idx) => (
                  <tr key={`l-${idx}`} className="border-b border-slate-100 even:bg-[#F9F5EC] hover:bg-slate-50/50">
                    <td className="py-1.5 px-2 text-center text-slate-500">{startIdx + idx + 1}</td>
                    <td className="py-1.5 px-2 font-semibold text-slate-800">{item.nama}</td>
                    <td className="py-1.5 px-2 text-slate-500 text-[9px]">{item.keterangan}</td>
                    <td className="py-1.5 px-2 text-center font-medium">{item.qty}</td>
                    <td className="py-1.5 px-2 text-right">Rp {fmt(item.harga)}</td>
                    <td className="py-1.5 px-2 text-right font-semibold text-slate-800">Rp {fmt(item.subtotal)}</td>
                  </tr>
                ));
              })()}

              {/* Baris Total Harga */}
              <tr className="bg-[#F9F5EC]">
                <td colSpan="4" className="py-2.5 px-3"></td>
                <td className="py-2.5 px-3 font-bold text-slate-700 text-right align-middle text-xs">Total Harga</td>
                <td className="py-2.5 px-3 text-right">
                  <span className="block text-[10px] text-[#8C6208] font-bold leading-none mb-0.5">Rp</span>
                  <span className="text-base font-black text-[#8C6208] leading-tight">
                    {fmt(totalHargaJualSemua)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Terbilang Box */}
          <div className="mb-6 bg-slate-50/50 p-2.5 rounded-lg border border-slate-100 text-xs">
            <p className="font-bold text-slate-700">Terbilang:</p>
            <p className="text-[10px] italic text-[#8C6208] font-medium mt-0.5">
              {terbilangRupiah(totalHargaJualSemua)}
            </p>
          </div>

          {/* Noted & TTD/Stempel Footer */}
          <div className="grid grid-cols-2 gap-4 items-start mt-6 text-xs">
            <div>
              <p className="font-bold text-slate-700 mb-1">Noted:</p>
              <p className="text-slate-500 text-[10px] mb-3">Pembayaran Harap Mencantumkan Detail Invoice</p>

              <div className="bg-slate-50 border border-slate-100 p-2.5 rounded-lg space-y-1 text-[9px] max-w-[280px]">
                <div className="flex">
                  <span className="w-24 text-slate-500">Alamat Transfer</span>
                  <span>: -</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-slate-500">Account</span>
                  <span className="font-bold text-slate-700">: Apa Saja Bisa</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-slate-500">Bank</span>
                  <span className="font-bold text-slate-700">: BCA</span>
                </div>
                <div className="flex">
                  <span className="w-24 text-slate-500">No Rekening</span>
                  <span className="font-bold text-slate-700">: 1741359516</span>
                </div>
              </div>
            </div>

            <div className="text-right flex flex-col items-end pr-4">
              <p className="text-[10px] text-slate-500 mb-1">Bogor, {formatTanggalIndo(now)}</p>
              
              <div className="relative w-48 h-20 my-1 flex justify-center items-center">
                <img 
                  src={companySettings.stempel_url || '/stempel-invoice.png'} 
                  alt="Stempel" 
                  className="absolute left-6 -top-1 pointer-events-none w-20 h-20 object-contain opacity-80" 
                />
                <img 
                  src={companySettings.ttd_url || '/ttd-invoice.png'} 
                  alt="Tanda Tangan" 
                  className="w-24 h-16 object-contain relative z-10" 
                />
              </div>

              <div className="text-center w-48 border-t border-slate-200 pt-1">
                <p className="font-bold text-slate-800 text-[10px]">{companySettings.nama_direktur || 'Umar Syarif'}</p>
                <p className="text-slate-400 text-[8px] uppercase tracking-wider mt-0.5">DIREKTUR</p>
              </div>
            </div>
          </div>

          {/* Bottom Gold Line */}
          <div className="border-b-[2px] border-[#D4AF37] mt-8"></div>
        </div>

        {/* Modal Footer Controls (Screen only) */}
        <div className="px-6 py-4 border-t border-[#E6D5B8] bg-[#F9F5EC] flex flex-col sm:flex-row items-center justify-between gap-3 no-print">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto border-[#E6D5B8] text-[#7A695B] hover:bg-white text-xs font-bold rounded-xl"
          >
            Kembali Edit Form
          </Button>

          <Button
            onClick={() => {
              onClose();
              if (onProceedToCheckout) onProceedToCheckout();
            }}
            className="w-full sm:w-auto bg-[#302621] hover:bg-[#1A1411] text-white font-bold text-xs px-8 h-11 rounded-xl shadow-lg gap-2 cursor-pointer"
          >
            <span>Lanjut Proses Pesanan & Pembayaran</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-invoice, #printable-invoice * {
            visibility: visible;
          }
          #printable-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
          }
          .no-print {
            display: none !important;
          }
        }
      `}} />
    </div>
  );
}
